import { Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { PlatformRequirementDocumentVerdict } from '@client/graphql/__generated__/types';
import { FC } from 'react';
import { match } from 'ts-pattern';
import {
  ChecklistIssueView,
  ChecklistIssuesList,
} from '~/common/pages/Compliance/components/ChecklistIssuesList';
import { DocumentSuggestionsList } from './DocumentSuggestionsList';
import { SuccessfulChecksList } from './SuccessfulChecksList';
import {
  CompliancePlatformRequirementIssue,
  ComplianceSuccessfulCheck,
  ComplianceSuggestion,
} from './types';

/**
 * Maps the agent-review checklist mismatch payload into the shared
 * ChecklistIssuesList view-model using only persisted evaluation evidence.
 */
const toChecklistIssueView = (
  issue: CompliancePlatformRequirementIssue
): ChecklistIssueView => {
  return {
    publicIssueId: issue.publicIssueId,
    status: issue.status,
    groupKey: issue.groupKey,
    evaluationRunId: issue.evaluationRunId,
    evaluatedAt: issue.evaluatedAt,
    isAgentActionable: issue.isAgentActionable,
    checklistItemName: issue.checklistItemName,
    checklistSectionName: issue.checklistSectionName,
    allowedDocumentCategories: issue.allowedDocumentCategories,
    allowedForms: issue.allowedForms,
    evaluationDocuments: issue.recordedEvaluationDocuments.map((document) => ({
      documentName: document.documentName,
      isProblemDocument: match(document.verdict)
        .with(PlatformRequirementDocumentVerdict.MISMATCHED, () => true)
        .with(
          PlatformRequirementDocumentVerdict.MATCHED,
          PlatformRequirementDocumentVerdict.UNCONFIGURED,
          PlatformRequirementDocumentVerdict.UNRESOLVED,
          () => false
        )
        .exhaustive(),
      ingestionDocumentId: document.ingestionDocumentId,
      platformDocumentId: document.platformDocumentId,
      contractDocuments: (document.contractDocuments ?? []).map(
        (contractDocument) => ({
          form: contractDocument.form ?? null,
          documentCategory: contractDocument.documentCategory ?? null,
        })
      ),
    })),
  };
};

interface DocumentIssuesPanelProps {
  suggestions: ComplianceSuggestion[];
  platformRequirementIssues: CompliancePlatformRequirementIssue[];
  successfulChecks: ComplianceSuccessfulCheck[];
  /** True once the active tab's detail has loaded. */
  hasDetail: boolean;
  loading: boolean;
  /** Called with a row's field ids when a suggestion/check row is clicked. */
  onSelectFields: (fieldIds: string[]) => void;
  /** Called with a suggestion to hide it (document-level ignore). */
  onIgnoreSuggestion: (suggestion: ComplianceSuggestion) => void;
  /** Called with an already-ignored suggestion to un-hide it (Undo). */
  onUndoIgnoreSuggestion: (suggestion: ComplianceSuggestion) => void;
  /** Called with a checklist mismatch to hide it. */
  onIgnorePlatformRequirementIssue: (
    issue: CompliancePlatformRequirementIssue
  ) => void;
  /** Called with an already-ignored checklist mismatch to un-hide it. */
  onUndoIgnorePlatformRequirementIssue: (
    issue: CompliancePlatformRequirementIssue
  ) => void;
  /** True while a document-level ignore mutation is in flight. */
  isIgnoring: boolean;
  /** True while a checklist mismatch ignore mutation is in flight. */
  isIgnoringPlatformRequirementIssue: boolean;
}

/**
 * The selected tab's suggestions, checklist mismatches, and passing checks
 * (derived client-side from the raw detail). Lives in the center column beside
 * the PDF in the redesigned layout — the checklist-level action buttons moved to
 * the title bar, so this panel is read/ignore only.
 *
 * Clicking a suggestion/check row calls `onSelectFields(row.fieldIds)`, which
 * the controller lifts into `activeFieldId` to scroll/center the PDF.
 */
export const DocumentIssuesPanel: FC<DocumentIssuesPanelProps> = ({
  suggestions,
  platformRequirementIssues,
  successfulChecks,
  hasDetail,
  loading,
  onSelectFields,
  onIgnoreSuggestion,
  onUndoIgnoreSuggestion,
  onIgnorePlatformRequirementIssue,
  onUndoIgnorePlatformRequirementIssue,
  isIgnoring,
  isIgnoringPlatformRequirementIssue,
}) => {
  if (loading && !hasDetail) {
    return (
      <Flex justify="center" py={8}>
        <Spinner color="compliance.loading.text" />
      </Flex>
    );
  }

  const hasFindings =
    suggestions.length > 0 ||
    platformRequirementIssues.length > 0 ||
    successfulChecks.length > 0;

  if (!hasFindings) {
    return (
      <Text color="compliance.text.muted" fontSize="sm">
        No validation suggestions, checklist suggestions, or checks for this
        document.
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={5}>
      <DocumentSuggestionsList
        isIgnoring={isIgnoring}
        suggestions={suggestions}
        onIgnore={onIgnoreSuggestion}
        onSelectFields={onSelectFields}
        onUndoIgnore={onUndoIgnoreSuggestion}
      />
      {platformRequirementIssues.length > 0 ? (
        <VStack align="stretch" spacing={2}>
          <Text
            color="compliance.text.secondary"
            fontSize="sm"
            fontWeight="semibold"
          >
            Checklist issues
          </Text>
          <ChecklistIssuesList
            actions={{
              isIgnoring: isIgnoringPlatformRequirementIssue,
              onIgnore: (view) => {
                const issue = platformRequirementIssues.find(
                  (candidate) => candidate.publicIssueId === view.publicIssueId
                );
                if (issue) {
                  onIgnorePlatformRequirementIssue(issue);
                }
              },
              onUndoIgnore: (view) => {
                const issue = platformRequirementIssues.find(
                  (candidate) => candidate.publicIssueId === view.publicIssueId
                );
                if (issue) {
                  onUndoIgnorePlatformRequirementIssue(issue);
                }
              },
            }}
            evaluationStatus="evaluated"
            issues={platformRequirementIssues.map(toChecklistIssueView)}
          />
        </VStack>
      ) : null}
      <SuccessfulChecksList
        checks={successfulChecks}
        onSelectFields={onSelectFields}
      />
    </VStack>
  );
};
